<script setup lang="ts">
import { computed } from "vue";
import { UiCard, UiText, UiTooltip, UiInfoBox, UiChip } from "@shopexperts/shared";
import type { AdminWalletStatisticsOverview } from "@shopexperts/legacy-api";
import { formatCurrency } from "@/v2Projects/utils/display";
import { LIABILITY_COLORS } from "./walletStatisticsDisplay";
import WalletLiabilityBar, { type LiabilitySegment } from "./WalletLiabilityBar.vue";

const props = defineProps<{
  overview?: AdminWalletStatisticsOverview;
  isLoading: boolean;
  error?: unknown;
  /** Currency the tab is currently scoped to (undefined = platform default). */
  currency?: string;
  /** Sort column the balances list is currently on, to highlight the matching segment. */
  activeSort?: string;
}>();

const emit = defineEmits<{
  sortBy: [key: "available" | "hold"];
  showNegative: [];
  changeCurrency: [currency: string];
}>();

const totals = computed(() => props.overview?.totals);

const segments = computed<LiabilitySegment[]>(() => {
  if (!totals.value) return [];
  return [
    {
      key: "available",
      label: "Available now",
      cents: totals.value.available_balance_cents,
      countLabel: expertsLabel(totals.value.experts_with_available_count),
      colorClass: LIABILITY_COLORS.available.bar,
      textClass: LIABILITY_COLORS.available.text,
    },
    {
      key: "hold",
      label: "On hold",
      cents: totals.value.hold_balance_cents,
      countLabel: expertsLabel(totals.value.experts_with_hold_count),
      colorClass: LIABILITY_COLORS.hold.bar,
      textClass: LIABILITY_COLORS.hold.text,
    },
  ];
});

function expertsLabel(count: number) {
  return `${count} expert${count === 1 ? "" : "s"}`;
}

const activeSegmentKey = computed(() =>
  props.activeSort === "available" || props.activeSort === "hold" ? props.activeSort : undefined,
);

const otherCurrencies = computed(() => {
  if (!props.overview) return [];
  return props.overview.by_currency.filter((bucket) => bucket.currency !== props.overview?.currency);
});

const otherOrganizations = computed(() => {
  const bucket = props.overview?.other_organizations;
  return bucket && bucket.wallet_count > 0 ? bucket : null;
});

const hasNegative = computed(() => (totals.value?.negative_balance_count ?? 0) > 0);

function onSelectSegment(key: string) {
  if (key === "available" || key === "hold") emit("sortBy", key);
}
</script>

<template>
  <UiInfoBox v-if="error" variant="error" title="Couldn't load wallet statistics">
    <UiText as="span" variant="p-small">
      <span>Something went wrong loading the statistics. Refresh to try again.</span>
    </UiText>
  </UiInfoBox>

  <div v-else-if="isLoading || !overview || !totals" class="h-36 animate-pulse rounded-xl bg-se-gray-100" />

  <UiCard v-else>
    <div class="flex flex-col gap-5 p-2 md:flex-row md:items-stretch md:gap-6">
      <!-- Hero: what the company owes experts right now -->
      <div class="flex min-w-[220px] flex-col justify-center gap-1 border-se-gray-200 md:border-r md:pr-6">
        <UiText
          as="span"
          variant="caption-normal"
          class="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400"
          text="Owed to experts"
        />
        <span class="text-[32px] font-extrabold leading-none tracking-tight text-se-dark tabular-nums">
          {{ formatCurrency(totals.total_owed_cents, overview.currency) }}
        </span>
        <UiText as="span" variant="p-small" class="mt-1 text-gray-500">
          {{ expertsLabel(totals.experts_owed_count) }} owed · {{ totals.wallet_count }} wallets in {{ overview.currency }}
        </UiText>
        <button v-if="hasNegative" type="button" class="mt-1.5 w-fit" @click="emit('showNegative')">
          <UiChip
            :text="`${totals.negative_balance_count} negative · ${formatCurrency(totals.negative_balance_cents, overview.currency)}`"
            size="xs"
            rounded="full"
            bg-color-class="bg-rose-50"
            text-color-class="text-rose-600"
            tooltip="Wallets below zero — experts who currently owe the company. Click to include them in the list."
          />
        </button>
      </div>

      <!-- Available vs on hold split -->
      <div class="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <UiText
          as="span"
          variant="caption-normal"
          class="text-[11px] font-semibold uppercase tracking-[0.03em] text-gray-400"
          text="Hover to inspect · click to sort the list below"
        />
        <WalletLiabilityBar
          :segments="segments"
          :currency="overview.currency"
          :active-key="activeSegmentKey"
          @select="onSelectSegment"
        />
      </div>
    </div>

    <!-- Reconciliation footnotes: other currencies and non-expert wallets -->
    <div
      v-if="otherCurrencies.length > 0 || otherOrganizations"
      class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-se-gray-200 p-2 pt-3"
    >
      <template v-if="otherCurrencies.length > 0">
        <UiText as="span" variant="p-small" class="text-gray-500" text="Also tracked:" />
        <button
          v-for="bucket in otherCurrencies"
          :key="bucket.currency"
          type="button"
          @click="emit('changeCurrency', bucket.currency)"
        >
          <UiChip
            :text="`${bucket.currency} ${formatCurrency(bucket.total_owed_cents, bucket.currency)} · ${bucket.wallet_count} wallets`"
            size="xs"
            rounded="full"
            variant="outline"
            :tooltip="`Switch the statistics to ${bucket.currency}`"
          />
        </button>
      </template>
      <UiTooltip
        v-if="otherOrganizations"
        content="Balances held by client or admin organizations — reported separately so expert totals reconcile against the raw wallet table."
      >
        <UiText as="span" variant="p-small" class="text-gray-400">
          + {{ formatCurrency(otherOrganizations.total_owed_cents, overview.currency) }} in
          {{ otherOrganizations.wallet_count }} non-expert wallets
        </UiText>
      </UiTooltip>
    </div>
  </UiCard>
</template>
